import {
  useEffect,
  useContext,
  createContext,
  useState,
  useReducer,
} from "react";
import { API } from "../service/api";
import moment from "moment";

import { showErrorToast, showSuccessToast } from "../utils/toast";

import makeCalendars from "../utils/calendars";
import makeSchedules from "../utils/schedules";

import { initialState, reducer } from "./store";
import { currentTime } from "../utils/timestamp";

const nCallContext = createContext();

export const useNCallContext = () => {
  return useContext(nCallContext);
};

export const NCallContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [code, setCode] = useState("");
  const [rocode, setRocode] = useState("");
  const [calendars, setCalendars] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [openSideBar, setOpenSideBar] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [activeSchedules, setActiveSchedules] = useState([]);

  // for disabling some inprogress feilds
  const [inprogressField, setInprogressField] = useState(false);

  const APP_RESET = () => {
    setCalendars([]);
    setSchedules([]);
    setOpenDetailModal(false);
    setOpenModal(false);
    setCode("");
    setRocode("");
    dispatch({ type: "RESET" });
  };

  useEffect(() => {
    getSchedules();
  }, [refresh]);

  const getSchedules = async () => {
    try {
      dispatch({ type: "LOADING", payload: true });
      let schedules = await API.getSchedule();
      // sorting based on dates
      schedules = schedules?.data.sort((a, b) => b.date.localeCompare(a.date));
      dispatch({
        type: "ADD-SHCEDULES",
        payload: { schedules: schedules },
      });

      let _schedules = makeSchedules(schedules);
      setSchedules(_schedules);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  const getReports = async () => {
    let _clientId = localStorage.getItem("companyId");

    if (_clientId) {
      try {
        const reports = await API.getAllReports();
        dispatch({
          type: "ADD-REPORTS",
          payload: { reports: reports.data },
        });
      } catch (error) {
        showErrorToast(error);
      }
    } else {
      return;
    }
  };

  const getActiveSchedules = async () => {
    // use for schedule tables only
    try {
      dispatch({ type: "LOADING", payload: true });
      let schedules = await API.getSchedule();
      // sorting based on dates
      schedules = schedules?.data.sort((a, b) => b.date.localeCompare(a.date));
      dispatch({
        type: "ADD-SHCEDULES",
        payload: { schedules: schedules },
      });
      let _schedules = schedules.filter((item) =>
        moment(item.endTime).isAfter(moment())
      );

      _schedules = _schedules.map((item) => ({
        ...item,
        scheduleStatus: currentTime(
          item.date,
          item.shifts.startTime,
          item.shifts.endTime
        )
          ? "in-progress"
          : "pending",
      }));
      setActiveSchedules(_schedules);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  const getAllClients = async () => {
    try {
      dispatch({ type: "LOADING", payload: true });
      const clients = await API.getCompanies();
      dispatch({
        type: "ADD-CLIENTS",
        payload: { clients: clients.data },
      });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  const postSchedules = async (data) => {
    data.company_id = localStorage.getItem("companyId");

    try {
      dispatch({ type: "LOADING", payload: true });
      const res = await API.postSchedule(data);
      if (!res.error) {
        //   showErrorToast(res.error.message);
        // } else {
        showSuccessToast(res.message);
        getSchedules();
      }
    } catch (error) {
      throw new Error(error.message);
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  const getEmployees = async () => {
    let clientId = localStorage.getItem("clientId");

    if (!clientId) {
      return;
    }

    try {
      dispatch({ type: "LOADING", payload: true });
      let employees = await API.getEmployees(clientId);

      // sorting based on firstname
      employees = employees?.CONTACTS?.Contact?.sort((a, b) =>
        a.Firstname.localeCompare(b.Firstname)
      );

      // Temp Aliases
      // if (employees.length > 0) {
      //   employees = employees.map((emp, i) => {
      //     if (i % 2 === 0) {
      //       return {
      //         ...emp,
      //         Alias: `alias-${i}`,
      //       };
      //     } else {
      //       return emp;
      //     }
      //   });
      // }

      dispatch({
        type: "ADD-EMPLOYEES",
        payload: { employees: employees },
      });
    } catch (error) {
      showErrorToast("Some Error Occured ");
      console.error(error);
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  const getDepartments = async () => {
    try {
      dispatch({ type: "LOADING", payload: true });
      const departments = await API.getDepartments();

      dispatch({
        type: "ADD-DEPARTMENTS",
        payload: { departments: departments.data },
      });
      let calendars = makeCalendars(departments.data);
      setCalendars(calendars);
    } catch (error) {
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  const getShifts = async () => {
    try {
      dispatch({ type: "LOADING", payload: true });
      const shifts = await API.getShifts();

      dispatch({
        type: "ADD-SHIFTS",
        payload: { shifts: shifts.data },
      });
    } catch (error) {
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  const getColors = async () => {
    try {
      dispatch({ type: "LOADING", payload: true });
      const colors = await API.getAllColors();

      dispatch({
        type: "ADD-COLORS",
        payload: { colors: colors.data },
      });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "LOADING", payload: false });
    }
  };

  useEffect(() => {
    const { schedules, employees, departments, clients } = state;

    if (localStorage.getItem("admin") === "admin") {
      if (schedules.length === 0) {
        getSchedules();
      }
      if (employees.length === 0) {
        getEmployees();
      }
      if (departments.length === 0) {
        getDepartments();
      }

      if (clients.length === 0) {
        getAllClients();
      }
    }
  }, []);

  const value = {
    code,
    setCode,
    rocode,
    setRocode,
    state,
    dispatch,
    calendars,
    setCalendars,
    setSchedules,
    schedules,
    openSideBar,
    setOpenSideBar,
    getEmployees,
    getDepartments,
    getSchedules,
    postSchedules,
    getShifts,
    openModal,
    setOpenModal,
    openDetailModal,
    setOpenDetailModal,
    getAllClients,
    APP_RESET,
    refresh,
    setRefresh,
    getColors,
    inprogressField,
    setInprogressField,
    getActiveSchedules,
    activeSchedules,
    getReports,
  };

  return (
    <nCallContext.Provider value={value}>{children}</nCallContext.Provider>
  );
};
